import React, { useMemo, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit3, Trash2, Download, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import crudService, { CrudEntity, CrudField } from '../../services/crudService'
import api from '../../services/api'

const PAGE_SIZES = [10, 25, 50, 100, 250, 500, 1000]

// Bandera para logs de depuración locales de esta página
const DEBUG_LOGS = false

// Normaliza valores para inputs HTML de fecha y fecha-hora
function toDateInputValue(v: any): string {
  if (!v) return ''
  try {
    if (typeof v === 'string') {
      const s = v.trim()
      // Ya viene en formato YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
      // ISO o similar => tomar la parte de fecha
      if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10)
      // Formato con espacio: YYYY-MM-DD HH:mm:ss => tomar solo la fecha
      if (/^\d{4}-\d{2}-\d{2} /.test(s)) return s.slice(0, 10)
      const d = new Date(s)
      if (isNaN(d.getTime())) return ''
      return d.toISOString().slice(0, 10)
    }
    if (v instanceof Date) return v.toISOString().slice(0, 10)
    return ''
  } catch {
    return ''
  }
}

function pad2(n: number) { return n < 10 ? `0${n}` : `${n}` }
// Helpers de fecha para filtros rápidos
function todayDate(): string {
  const d = new Date()
  const y = d.getFullYear()
  const MM = pad2(d.getMonth() + 1)
  const dd = pad2(d.getDate())
  return `${y}-${MM}-${dd}`
}

function startOfTodayLocal(): string {
  const d = new Date()
  const y = d.getFullYear()
  const MM = pad2(d.getMonth() + 1)
  const dd = pad2(d.getDate())
  return `${y}-${MM}-${dd}T00:00`
}

function endOfTodayLocal(): string {
  const d = new Date()
  const y = d.getFullYear()
  const MM = pad2(d.getMonth() + 1)
  const dd = pad2(d.getDate())
  return `${y}-${MM}-${dd}T23:59`
}

// Formatea fechas YYYY-MM-DD para mostrar sin aplicar zona horaria
function formatDateForDisplay(v: any): string {
  if (!v) return ''
  if (typeof v === 'string') {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (m) return `${m[3]}/${m[2]}/${m[1]}`
    // Si viene con tiempo, tomar solo la parte de fecha si comienza con YYYY-MM-DD
    const m2 = v.match(/^(\d{4})-(\d{2})-(\d{2})T/)
    if (m2) return `${m2[3]}/${m2[2]}/${m2[1]}`
    return v
  }
  try {
    const d = new Date(v)
    if (!isNaN(d.getTime())) return d.toLocaleDateString()
  } catch {}
  return String(v)
}

function toDatetimeLocalValue(v: any): string {
  if (!v) return ''
  try {
    if (typeof v === 'string') {
      const s = v.trim()
      // Si ya viene como YYYY-MM-DDTHH:mm[:ss][.sss] opcional Z, normalizar quitando Z y ms, quedándonos con HH:mm
      const m = s.replace(' ', 'T').replace(/Z$/, '')
      const m2 = m.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/)
      if (m2) return `${m2[1]}T${m2[2]}`
      // Intentar parsear y devolver en hora local sin zona
      const d = new Date(s)
      if (isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const MM = pad2(d.getMonth() + 1)
      const dd = pad2(d.getDate())
      const hh = pad2(d.getHours())
      const mm = pad2(d.getMinutes())
      return `${y}-${MM}-${dd}T${hh}:${mm}`
    }
    if (v instanceof Date) {
      const y = v.getFullYear()
      const MM = pad2(v.getMonth() + 1)
      const dd = pad2(v.getDate())
      const hh = pad2(v.getHours())
      const mm = pad2(v.getMinutes())
      return `${y}-${MM}-${dd}T${hh}:${mm}`
    }
    return ''
  } catch {
    return ''
  }
}

function getInputForType(field: CrudField, value: any, onChange: (v: any) => void, sizeClass: string = 'text-base') {
  const type = field.type
  switch (type) {
    case 'number':
      return <input className={`input input-bordered w-full ${sizeClass}`} type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
    case 'boolean':
      return (
        <select className={`select select-bordered w-full ${sizeClass}`} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}>
          <option value="">--</option>
          <option value="true">Sí</option>
          <option value="false">No</option>
        </select>
      )
    case 'date':
      return <input className={`input input-bordered w-full ${sizeClass}`} type="date" value={toDateInputValue(value)} onChange={(e) => onChange(e.target.value || null)} />
    case 'datetime':
      return <input className={`input input-bordered w-full ${sizeClass}`} type="datetime-local" value={toDatetimeLocalValue(value)} onChange={(e) => onChange(e.target.value || null)} />
    case 'text':
      return <textarea className={`textarea textarea-bordered w-full ${sizeClass}`} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    case 'select':
      if (field.select_options && field.select_options.length > 0) {
        return (
          <select className={`select select-bordered w-full ${sizeClass}`} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
            <option value="">--</option>
            {field.select_options.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        )
      }
      return <input className={`input input-bordered w-full ${sizeClass}`} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    case 'decimal': {
      const s = typeof field.scale === 'number' ? field.scale : 0
      const step = s <= 0 ? '1' : `0.${'0'.repeat(Math.max(0, s - 1))}1`
      return (
        <input
          className={`input input-bordered w-full ${sizeClass}`}
          type="number"
          step={step}
          value={value ?? ''}
          onChange={(e) => {
            const v = e.target.value
            onChange(v === '' ? null : Number(v))
          }}
        />
      )
    }
    case 'string':
    default:
      return <input className={`input input-bordered w-full ${sizeClass}`} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
  }
}

const RecordModal: React.FC<{
  title: string
  isOpen: boolean
  onClose: () => void
  fields: CrudField[]
  editableFieldNames: string[]
  initial?: Record<string, any>
  onSubmit: (values: Record<string, any>) => void
  crudId: string
}> = ({ title, isOpen, onClose, fields, editableFieldNames, initial, onSubmit, crudId }) => {
  const [values, setValues] = useState<Record<string, any>>(initial || {})
  const queryClient = useQueryClient()
  const fieldsSignature = useMemo(() => (fields || []).map(f => String(f.name)).sort().join('|'), [fields])

  // Cargar todas las opciones de relaciones en un solo payload determinista
  const { data: relationOptions } = useQuery({
    queryKey: ['crud-relations-all', crudId, fieldsSignature],
    queryFn: async () => {
      try {
        const res = await api.get(`crud-configs/${crudId}/data/relations`)
        const raw = res.data
        const payload = raw?.data ?? raw
        return payload || {}
      } catch (e) {
        console.error('[RecordModal] error cargando relaciones unificadas:', e)
        return {}
      }
    },
    enabled: isOpen,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  })

  // Logs de depuración para verificar datos de relaciones
  useEffect(() => {
    if (!isOpen) return
    if (DEBUG_LOGS) console.log('[RecordModal] relationOptions =>', relationOptions)
  }, [relationOptions, isOpen])

  // Forzar invalidación de queries de relaciones al abrir el modal
  useEffect(() => {
    if (!isOpen) return
    queryClient.invalidateQueries({ queryKey: ['crud-relations-all', crudId] })
    queryClient.invalidateQueries({ queryKey: ['crud-relations', crudId] })
  }, [isOpen, crudId, queryClient])

  const norm = (s: string) => String(s || '').replace(/[^\w_]/g, '_')

  useEffect(() => {
    // Mapear valores iniciales desde claves normalizadas => nombres originales de campos
    const mapped: Record<string, any> = {}
    for (const f of fields) {
      const k = String(f.name)
      mapped[k] = initial ? initial[norm(k)] : undefined
    }
    // Mantener cualquier valor ya editado si existe
    setValues((prev) => ({ ...mapped, ...Object.fromEntries(Object.entries(prev).filter(([k]) => !mapped.hasOwnProperty(k))) }))
  }, [initial, fields, isOpen])
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  const isActiveRelation = (fieldName: string) => {
    if (!relationOptions) return false
    const opts = (relationOptions as any)
    return Object.prototype.hasOwnProperty.call(opts, fieldName)
  }

  if (!isOpen) return null

  // Ocultar campos que no deben mostrarse en el formulario
  const editable = fields
    .filter(f => editableFieldNames.includes(f.name))
    .filter((f: any) => (f as any).show_in_form !== false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(values)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="card w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="card-header relative">
          <h2 className="text-lg font-semibold text-center w-full">{title}</h2>
          <button
            type="button"
            aria-label="Cerrar"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {editable.map((field) => (
                <div key={field.name} className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">{field.name}</label>
                  {isActiveRelation(field.name) ? (
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={values[field.name] || ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    >
                      <option value="">Seleccionar {field.name.replace('_id', '')}</option>
                      {relationOptions && (relationOptions as any)[field.name] && (relationOptions as any)[field.name].map((opt: any) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label ?? `ID: ${opt.value}`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    getInputForType(field, values[field.name], (v) => setValues((prev) => ({ ...prev, [field.name]: v })))
                  )}
                </div>
              ))}
            </div>
            <div className="card-footer justify-end gap-2">
              <button type="button" className="btn-blue-flat btn-square btn-compact" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn-blue-flat btn-square btn-compact">Guardar</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

const CrudDataPage: React.FC = () => {
  const params = useParams()
  const queryClient = useQueryClient()
  // Normalizar crudId a string siempre para evitar errores de tipo
  const crudId = String(params.id || '')
 

  // UI state
  // Filtros avanzados por campo
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<Record<string, any>>({})
  const [tempFilters, setTempFilters] = useState<Record<string, any>>({})
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [sortBy, setSortBy] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const [showCreate, setShowCreate] = useState(false)
  const [editRow, setEditRow] = useState<{ pk: string | number; data: Record<string, any> } | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [pendingDeletePk, setPendingDeletePk] = useState<string | number | null>(null)
  const [viewRow, setViewRow] = useState<Record<string, any> | null>(null)

  // Efecto para cerrar modal de vista con Escape
  useEffect(() => {
    if (!viewRow) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setViewRow(null)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [viewRow])

  // Filtros activos (para chips)
  const activeFilterEntries = useMemo<[string, any][]>(() => {
    return (Object.entries(filters || {}) as [string, any][])
      .filter(([_, v]) => v !== null && v !== undefined && String(v).trim() !== '')
  }, [filters])

  const { data: crud, isLoading: loadingCrud, error: crudError } = useQuery<CrudEntity>({
    queryKey: ['crud', crudId],
    queryFn: () => crudService.getCrud(crudId),
    // Configurar para que se refresque más frecuentemente
    // Esto es crucial después de renombrar campos
    refetchOnWindowFocus: true,
    staleTime: 5000,
    // Forzar revalidación cuando se vuelve a montar el componente
    refetchOnMount: 'always'
  })

  // Cargar opciones de relaciones para mostrar en la tabla
  const fieldsSignature = useMemo(() => (crud?.fields || []).map(f => String(f.name)).sort().join('|'), [crud?.fields])
  const { data: relationOptions } = useQuery({
    queryKey: ['crud-relations-all', crudId, fieldsSignature],
    queryFn: async () => {
      try {
        const res = await api.get(`crud-configs/${crudId}/data/relations`)
        const raw = res.data
        const payload = raw?.data ?? raw
        return payload || {}
      } catch (e) {
        console.error('[CrudDataPage] error cargando relaciones unificadas:', e)
        return {}
      }
    },
    enabled: !!crudId && !!crud?.fields,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  })

  const { data: listData, isLoading: loadingList, error: listError } = useQuery<{ items: any[]; total: number; page: number; pageSize: number}>({
    queryKey: ['crud-data', crudId, { page, pageSize, filters, sortBy, sortDir }],
    queryFn: () => crudService.listData(crudId, { page, pageSize, filters, sortBy, sortDir }),
    enabled: !!crudId,
    keepPreviousData: true,
    // Forzar refresco cuando cambie la configuración del CRUD
    // Esto es crucial para asegurar que se usen los nombres de campo actualizados
    refetchOnWindowFocus: true,
    // Reducir el staleTime para asegurar que los datos se refresque después de renombrar campos
    staleTime: 5000,
    // Asegurar refetch al montarse (especialmente tras cambios de configuración/renombres)
    refetchOnMount: 'always'
  })

  const createMutation = useMutation({
    mutationFn: (values: Record<string, any>) => crudService.createData(crudId, values),
    onSuccess: () => {
      toast.success('Registro creado')
      queryClient.invalidateQueries({ queryKey: ['crud-data', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud-relations-all', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud-relations', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud', crudId] })
      setShowCreate(false)
    },
    onError: () => toast.error('Error al crear registro'),
  })

  const updateMutation = useMutation({
    mutationFn: (payload: { pk: string | number; values: Record<string, any> }) => crudService.updateData(crudId, payload.pk, payload.values),
    onSuccess: () => {
      toast.success('Registro actualizado')
      queryClient.invalidateQueries({ queryKey: ['crud-data', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud-relations-all', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud-relations', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud', crudId] })
      setEditRow(null)
    },
    onError: () => toast.error('Error al actualizar registro'),
  })

  const deleteMutation = useMutation({
    mutationFn: (pk: string | number) => crudService.deleteData(crudId, pk),
    onSuccess: () => {
      toast.success('Registro eliminado')
      queryClient.invalidateQueries({ queryKey: ['crud-data', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud-relations-all', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud-relations', crudId] })
      queryClient.invalidateQueries({ queryKey: ['crud', crudId] })
    },
    onError: () => toast.error('Error al eliminar registro'),
  })


  const normalizeKey = (s: string) => String(s || '').replace(/[^\w_ ]/g, '_')
  
  // Función para obtener el valor descriptivo de una relación
  const getRelationDisplayValue = (fieldName: string, value: any): string => {
    if (!value || !relationOptions || !(relationOptions as any)[fieldName]) {
      return String(value ?? '')
    }
    
    const options = (relationOptions as any)[fieldName]
    const option = options.find((opt: any) => String(opt.value) === String(value))
    return option?.label ?? `ID: ${value}`
  }
  
  // Columnas visibles con etiqueta original y clave normalizada para acceso a datos
  const displayColumnsMeta = useMemo(() => {
    const display = (crud?.display_fields || []).map((n) => String(n))
    const fields = crud?.fields || []
    const metas: { label: string; key: string }[] = []
    for (const name of display) {
      const f = fields.find((x) => String(x.name) === name)
      if (f) metas.push({ label: f.name, key: normalizeKey(f.name) })
    }
    return metas
  }, [crud])
  const sortableColumns = useMemo(() => new Set((crud?.sortable_fields || []).map((s) => String(s))), [crud])

  const primaryKeyLabel = useMemo(() => crud?.fields.find(f => f.is_primary_key)?.name, [crud])
  const primaryKeyKey = useMemo(() => primaryKeyLabel ? normalizeKey(primaryKeyLabel) : undefined, [primaryKeyLabel])
  const editableFieldNames = useMemo(() => crud?.editable_fields || [], [crud])

  const viewRowPk = useMemo(() => {
    if (!viewRow || !primaryKeyKey) return null;
    return viewRow[primaryKeyKey];
  }, [viewRow, primaryKeyKey]);

  const { data: viewRowData, isLoading: isLoadingViewRowData } = useQuery({
    queryKey: ['crud-data-single', crudId, viewRowPk],
    queryFn: () => {
      if (!viewRowPk) return Promise.resolve(null);
      return crudService.getData(crudId, viewRowPk);
    },
    enabled: !!viewRowPk,
    refetchOnWindowFocus: false,
  });

  const handleSort = (col: string) => {
    if (!sortableColumns.has(col)) return
    if (sortBy !== col) {
      setSortBy(col)
      setSortDir('asc')
    } else {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    }
  }

  const onExportCsv = async () => {
    try {
      const csv = await crudService.exportCsv(crudId, { 
        filters, 
        allFields: true,
        page,
        pageSize,
        sortBy,
        sortDir
      })
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${crud?.table_name || 'datos'}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast.error('Error al exportar CSV')
    }
  }

  if (crudError) {
    return <div className="p-6"><p className="text-red-600">Error al cargar la configuración</p></div>
  }

  if (listError) {
    return <div className="p-6"><p className="text-red-600">Error al cargar los datos</p></div>
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <div className="flex items-center">
            {/* Izquierda: título */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{crud?.name || 'CRUD'}</h1>
              <p className="text-sm text-gray-600">Tabla: {crud?.table_name}</p>
            </div>
            {/* Centro: botón Buscar */}
            <div className="flex-1 flex justify-center">
              <button
                className="btn-accent-flat btn-square btn-compact"
                onClick={() => {
                  // Cargar filtros actuales sin prellenar valores por defecto
                  setTempFilters(filters || {})
                  setShowFilters((v) => !v)
                }}
              >
                <Filter className="h-4 w-4" />
                Buscar
              </button>
            </div>
            {/* Derecha: acciones */}
            <div className="flex-1 flex justify-end gap-2">
              {crud?.permissions.create && (
                <button className="btn-blue-flat btn-square btn-compact" onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4" />
                  Nuevo
                </button>
              )}
              <button className="btn-blue-flat btn-square btn-compact" onClick={onExportCsv}>
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
            </div>
          </div>
        </div>

        <div className="card-body space-y-4">
          {/* Panel de filtros */}
          {showFilters && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-base font-semibold text-gray-700 mb-4">Filtros avanzados</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {crud?.fields
                  .filter((f) => (crud?.searchable_fields || []).includes(f.name))
                  .map((field) => (
                    <div key={field.name} className="space-y-2">
                      <label className="block text-base font-semibold text-gray-700">{field.name}</label>
                      {(field.type === 'date' || field.type === 'datetime') ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-base font-semibold text-gray-700">Desde</label>
                              {field.type === 'date' ? (
                                <input
                                  className="input input-bordered w-full text-base"
                                  type="date"
                                  placeholder="YYYY-MM-DD"
                                  value={toDateInputValue(tempFilters[`${field.name}__from`] ?? '')}
                                  onChange={(e) => setTempFilters((prev) => ({ ...prev, [`${field.name}__from`]: e.target.value || '' }))}
                                />
                              ) : (
                                <input
                                  className="input input-bordered w-full text-base"
                                  type="datetime-local"
                                  placeholder="YYYY-MM-DDTHH:mm"
                                  value={toDatetimeLocalValue(tempFilters[`${field.name}__from`] ?? '')}
                                  onChange={(e) => setTempFilters((prev) => ({ ...prev, [`${field.name}__from`]: e.target.value || '' }))}
                                />
                              )}
                            </div>
                            <div>
                              <label className="block text-base font-semibold text-gray-700">Hasta</label>
                              {field.type === 'date' ? (
                                <input
                                  className="input input-bordered w-full text-base"
                                  type="date"
                                  placeholder="YYYY-MM-DD"
                                  value={toDateInputValue(tempFilters[`${field.name}__to`] ?? '')}
                                  onChange={(e) => setTempFilters((prev) => ({ ...prev, [`${field.name}__to`]: e.target.value || '' }))}
                                />
                              ) : (
                                <input
                                  className="input input-bordered w-full text-base"
                                  type="datetime-local"
                                  placeholder="YYYY-MM-DDTHH:mm"
                                  value={toDatetimeLocalValue(tempFilters[`${field.name}__to`] ?? '')}
                                  onChange={(e) => setTempFilters((prev) => ({ ...prev, [`${field.name}__to`]: e.target.value || '' }))}
                                />
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className="btn-blue-flat btn-square btn-compact"
                              onClick={() => {
                                if (field.type === 'date') {
                                  const t = todayDate()
                                  setTempFilters((prev) => ({ ...prev, [`${field.name}__from`]: t, [`${field.name}__to`]: t }))
                                } else {
                                  setTempFilters((prev) => ({ ...prev, [`${field.name}__from`]: startOfTodayLocal(), [`${field.name}__to`]: endOfTodayLocal() }))
                                }
                              }}
                            >
                              Hoy
                            </button>
                            <button
                              type="button"
                              className="btn-blue-flat btn-square btn-compact"
                              onClick={() => {
                                setTempFilters((prev) => {
                                  const next = { ...prev }
                                  delete next[`${field.name}__from`]
                                  delete next[`${field.name}__to`]
                                  return next
                                })
                              }}
                            >
                              Limpiar
                            </button>
                          </div>
                        </div>
                      ) : relationOptions && (relationOptions as any)[field.name] ? (
                        <select
                          className="select select-bordered w-full text-lg"
                          value={tempFilters[field.name] ?? ''}
                          onChange={(e) => setTempFilters((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        >
                          <option value="">-- Seleccionar {field.name.replace('_id', '')} --</option>
                          {(relationOptions as any)[field.name].map((opt: any) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label ?? `ID: ${opt.value}`}
                            </option>
                          ))}
                        </select>
                      ) : (
                        getInputForType(
                          field,
                          tempFilters[field.name] ?? '',
                          (v) => setTempFilters((prev) => ({ ...prev, [field.name]: v })),
                          'text-lg'
                        )
                      )}
                    </div>
                  ))}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="btn-blue-flat btn-square btn-compact"
                  onClick={() => {
                    setTempFilters({})
                  }}
                >
                  Limpiar
                </button>
                <button
                  className="btn-blue-flat btn-square btn-compact"
                  onClick={() => {
                    // No enviar filtros vacíos
                    const cleaned = Object.fromEntries(
                      Object.entries(tempFilters).filter(([_k, v]) => v !== null && v !== undefined && String(v).trim() !== '')
                    )
                    setFilters(cleaned)
                    setPage(1)
                    setShowFilters(false)
                  }}
                >
                  Aplicar filtros
                </button>
              </div>
            </div>
          )}

          {/* Chips de filtros activos */}
          {activeFilterEntries.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Filtros:</span>
              {activeFilterEntries.map(([key, val]) => (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                >
                  <span className="text-xs font-medium">{key}: {String(val)}</span>
                  <button
                    type="button"
                    className="btn-blue-flat btn-square btn-compact"
                    aria-label={`Quitar filtro ${key}`}
                    onClick={() => {
                      setFilters((prev) => {
                        const next = { ...(prev || {}) }
                        delete next[key]
                        return next
                      })
                      setPage(1)
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="btn-blue-flat btn-square btn-compact"
                onClick={() => { setFilters({}); setPage(1) }}
              >
                Limpiar todo
              </button>
            </div>
          )}

          {/* Table */}
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {displayColumnsMeta.map(col => (
                    <th key={col.key}>
                      <button className={`inline-flex items-center gap-1 ${sortableColumns.has(col.label.trim()) ? 'hover:underline' : ''}`} onClick={() => handleSort(col.label)} disabled={!sortableColumns.has(col.label.trim())}>
                        {col.label}
                        {sortBy !== col.label && sortableColumns.has(col.label.trim()) && <ArrowUpDown className="h-3 w-3" />}
                        {sortBy === col.label && (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                      </button>
                    </th>
                  ))}
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loadingCrud || loadingList ? (
                  <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={100}>Cargando...</td></tr>
                ) : (listData?.items || []).length === 0 ? (
                  <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={100}>Sin datos</td></tr>
                ) : (
                  listData!.items.map((row: any, idx: number) => (
                    <tr key={idx}>
                      {displayColumnsMeta.map((col) => {
                        const fieldDef = crud?.fields.find(f => f.name === col.label);
                        const value = row[col.key];
                        
                        // Formatear según el tipo de campo
                        let displayValue = String(value ?? '');
                        
                        if (value && fieldDef) {
                          if (fieldDef.type === 'date' && value) {
                            displayValue = formatDateForDisplay(value)
                          } else if (fieldDef.type === 'datetime' && value) {
                            try {
                              displayValue = new Date(value).toLocaleString();
                            } catch (e) {}
                          } else if (fieldDef.type === 'select' && fieldDef.select_options && fieldDef.select_options.length > 0) {
                            // Para campos select normales, mostrar la opción seleccionada
                            const option = fieldDef.select_options.find((opt: string) => String(opt) === String(value));
                            displayValue = option ?? String(value);
                          } else if (relationOptions && (relationOptions as any)[fieldDef.name]) {
                            // Para campos de relación, mostrar el valor descriptivo
                            displayValue = getRelationDisplayValue(fieldDef.name, value);
                          }
                        }
                        
                        return (
                          <td key={col.key} className="text-sm text-gray-700">{displayValue}</td>
                        );
                      })}
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          <button className="btn-blue-flat btn-square btn-compact" title="Ver detalles" onClick={() => setViewRow(row)}>
                            <Eye className="h-4 w-4" />
                          </button>
                          {crud?.permissions.update && (
                            <button className="btn-blue-flat btn-square btn-compact" title="Editar" onClick={() => setEditRow({ pk: row[primaryKeyKey!], data: row })}>
                              <Edit3 className="h-4 w-4" />
                            </button>
                          )}
                          {crud?.permissions.delete && (
                            <button className="btn-blue-flat btn-square btn-compact" title="Eliminar" onClick={() => {
                              if (!primaryKeyKey) return
                              setPendingDeletePk(row[primaryKeyKey])
                              setShowDeleteConfirm(true)
                            }}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación inferior */}
        <div className="card-footer justify-between">
          {(() => {
            const total = listData?.total || 0
            const totalPages = Math.max(1, Math.ceil(total / pageSize))
            const from = total === 0 ? 0 : (page - 1) * pageSize + 1
            const to = Math.min(total, page * pageSize)

            // Construcción de páginas con elipsis
            const pageItems: (number | 'ellipsis')[] = []
            if (totalPages <= 9) {
              for (let i = 1; i <= totalPages; i++) pageItems.push(i)
            } else {
              pageItems.push(1)
              const left = Math.max(2, page - 2)
              const right = Math.min(totalPages - 1, page + 2)
              if (left > 2) pageItems.push('ellipsis')
              for (let i = left; i <= right; i++) pageItems.push(i)
              if (right < totalPages - 1) pageItems.push('ellipsis')
              pageItems.push(totalPages)
            }

            return (
              <>
                <div className="text-sm text-gray-600">
                  Mostrando {from} a {to} de {total} entradas
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Mostrar</span>
                    <select
                      className="select select-bordered select-compact"
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                    >
                      {PAGE_SIZES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-600">entradas</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      className="pager-btn"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      «
                    </button>
                    {pageItems.map((it, idx) => (
                      typeof it === 'number' ? (
                        <button
                          key={`p-${it}`}
                          className={`pager-btn ${it === page ? 'pager-btn-active' : ''}`}
                          onClick={() => setPage(it)}
                        >
                          {it}
                        </button>
                      ) : (
                        <span
                          key={`e-${idx}`}
                          className="inline-flex items-center justify-center h-8 px-2 rounded-[4px] border border-gray-300 text-gray-400"
                        >
                          …
                        </span>
                      )
                    ))}
                    <button
                      className="pager-btn"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      »
                    </button>
                  </div>
                </div>
              </>
            )
          })()}
        </div>
      </div>

      {/* Modals */}
      <RecordModal
        title="Nuevo registro"
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        fields={crud?.fields || []}
        editableFieldNames={editableFieldNames}
        onSubmit={(values) => createMutation.mutate(values)}
        crudId={crudId}
      />

      <RecordModal
        title="Editar registro"
        isOpen={!!editRow}
        onClose={() => setEditRow(null)}
        fields={crud?.fields || []}
        editableFieldNames={editableFieldNames}
        initial={editRow?.data}
        onSubmit={(values) => editRow && updateMutation.mutate({ pk: editRow.pk, values })}
        crudId={crudId}
      />

      {showDeleteConfirm && pendingDeletePk !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="card w-full max-w-md">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-center w-full">Confirmar eliminación</h2>
            </div>
            <div className="card-body">
              <p className="text-sm text-gray-700">¿Eliminar registro?</p>
            </div>
            <div className="card-footer justify-end gap-2">
              <button
                type="button"
                className="btn-blue-flat btn-square btn-compact"
                disabled={deleteMutation.isLoading}
                onClick={() => { setShowDeleteConfirm(false); setPendingDeletePk(null) }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-blue-flat btn-square btn-compact"
                disabled={deleteMutation.isLoading}
                onClick={async () => {
                  if (pendingDeletePk == null) return
                  try {
                    await deleteMutation.mutateAsync(pendingDeletePk)
                    setShowDeleteConfirm(false)
                    setPendingDeletePk(null)
                  } catch (e) {
                    // El error ya es notificado vía toast en onError
                  }
                }}
              >
                {deleteMutation.isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="card w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="card-header relative">
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <Eye className="h-5 w-5 text-blue-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Detalles del Registro</h2>
                </div>
                <span className="text-sm text-gray-600 ml-3">
                  {crud?.name || 'CRUD'} - ID: {viewRow[primaryKeyKey!] || 'N/A'}
                </span>
              </div>
              <button
                type="button"
                aria-label="Cerrar"
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                onClick={() => setViewRow(null)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="card-body">
            {isLoadingViewRowData || !viewRowData?.data ? (
                <div className="flex justify-center items-center p-8">
                  <p>Cargando datos completos...</p>
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-4">
                {(crud?.fields || []).map((fieldDef) => {
                  const value = viewRowData.data[normalizeKey(fieldDef.name)];
                  
                  // Formatear según el tipo de campo
                  let displayValue = String(value ?? '');
                  
                  if (value && fieldDef) {
                    if (fieldDef.type === 'date' && value) {
                      displayValue = formatDateForDisplay(value)
                    } else if (fieldDef.type === 'datetime' && value) {
                      try {
                        displayValue = new Date(value).toLocaleString();
                      } catch (e) {}
                    } else if (fieldDef.type === 'select' && fieldDef.select_options && fieldDef.select_options.length > 0) {
                      const option = fieldDef.select_options.find((opt: string) => String(opt) === String(value));
                      displayValue = option ?? String(value);
                    } else if (relationOptions && (relationOptions as any)[fieldDef.name]) {
                      displayValue = getRelationDisplayValue(fieldDef.name, value);
                    }
                  }
                  
                  return (
                    <div key={fieldDef.name} className="space-y-2">
                      <label className="block text-base font-medium text-gray-700">{fieldDef.name}</label>
                      <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-base text-gray-900 min-h-[38px] flex items-center">
                        {displayValue}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
            <div className="card-footer justify-end gap-2">
              <button 
                type="button" 
                className="btn-blue-flat btn-square btn-compact" 
                onClick={() => setViewRow(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CrudDataPage
